import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { type ChosenSlot, toBookingInput } from 'components/tours/TourBookingSteps';
import { useTourAvailability } from 'components/tours/useTourAvailability';
import { useWebTourBookingGate } from 'components/tours/webTourBookingFlag';

type PdpTourCardProps = {
  listingId?: string | null;
  onChoose: (slot?: ChosenSlot) => void;
};

export const PdpTourCard = ({ listingId, onChoose }: PdpTourCardProps) => {
  const gate = useWebTourBookingGate();
  const enabled = gate.isResolved && gate.isEnabled && Boolean(listingId);
  const { availability, isLoading, error } = useTourAvailability(listingId, {
    enabled,
    refetchInterval: 60_000,
  });
  const timezone = availability?.timezone;
  const days =
    enabled && !error && availability && timezone
      ? availability.days.map((day) => ({
          ...day,
          slots: day.slots.filter(
            (slot) => slot.isNow || new Date(slot.startTime).getTime() > Date.now(),
          ),
        }))
      : [];
  const day = days.find((day) => day.slots.some((slot) => slot.enabled));
  const firstAvailableIndex = day?.slots.findIndex((slot) => slot.enabled) ?? 0;
  const slots = day?.slots.slice(firstAvailableIndex, firstAvailableIndex + 3) ?? [];
  const openPicker = () => onChoose();
  let subtitle = 'Choose a time to tour';
  if (!gate.isResolved || isLoading) subtitle = 'Loading tour times…';
  else if (enabled && (error || !availability?.timezone))
    subtitle = 'Tour times are temporarily unavailable';
  else if (enabled && !day) subtitle = 'No tour times available';
  else if (day) {
    subtitle = new Date(`${day.date}T12:00:00Z`).toLocaleDateString('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'long',
      day: 'numeric',
    });
    const date = Number(day.date.slice(-2));
    const ordinal =
      date % 100 >= 11 && date % 100 <= 13
        ? 'th'
        : ({ 1: 'st', 2: 'nd', 3: 'rd' }[date % 10] ?? 'th');
    subtitle += ordinal;
  }

  return (
    <Col
      as="section"
      aria-label="Book a self tour"
      className="@container w-full gap-[14px] rounded-2xl bg-od-bg-tertiary px-6 py-5"
    >
      <Col className="gap-2">
        <Text as="h2" className="text-subheader-xs text-od-content-primary">
          Book a self tour
        </Text>
        <Text as="p" aria-live="polite" className="text-label-sm text-od-content-secondary">
          {subtitle}
        </Text>
      </Col>
      {timezone && slots.length > 0 && (
        <Row className="w-full gap-2 @[22rem]:gap-3">
          {slots.map((slot) => {
            const chosen = toBookingInput(slot, timezone);
            const label = new Date(slot.startTime)
              .toLocaleTimeString('en-US', {
                timeZone: timezone,
                hour: 'numeric',
                minute: '2-digit',
              })
              .toLowerCase();
            return (
              <Button
                key={slot.startTime}
                type="button"
                variant="secondary"
                analyticsName="cosmos-marketplace-pdp-tour-slot"
                aria-label={`${label} on ${day?.date}${slot.enabled ? '' : ' — unavailable'}`}
                disabled={!slot.enabled}
                onClick={() => onChoose(chosen)}
                className={cn(
                  'h-[46px] min-w-0 flex-1 gap-1 rounded-xl border border-od-border-primary px-1 text-label-xs shadow-none @[18rem]:gap-2 @[18rem]:text-label-sm',
                  slot.enabled
                    ? 'bg-od-bg-primary text-od-content-primary'
                    : 'bg-od-bg-secondary text-od-content-secondary opacity-100 disabled:opacity-100',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'size-2 shrink-0 rounded-full',
                    slot.enabled ? 'bg-od-bg-success' : 'bg-od-bg-error',
                  )}
                />
                {label}
              </Button>
            );
          })}
        </Row>
      )}
      <Button
        type="button"
        onClick={openPicker}
        analyticsName="cosmos-marketplace-self-tour-cta"
        aria-label="Pick another time"
        variant="secondary"
        className="h-12 w-full rounded-xl px-3 text-label-sm"
      >
        Pick another time
      </Button>
    </Col>
  );
};
